import React, {ChangeEvent, FC, useEffect, useState, useRef} from 'react';
import {IconButton, InputBase, Paper, InputBaseProps, Box} from '@mui/material';
import {keyframes} from '@emotion/react';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import SearchBarResults, {SearchResultItem} from './SearchBarResults';

export type Size = 'small' | 'medium' | 'large' | 'full';

export interface SearchBarProps
  extends Omit<InputBaseProps, 'size' | 'results'> {
  placeholder?: string;
  size?: Size;
  applyShadow?: boolean;
  onSearch?: (query: string) => void;
  results?: SearchResultItem[];
  onResultSelect?: (item: SearchResultItem) => void;
  loading?: boolean;
  searchOnKeyUp?: boolean;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  searchDelay?: number;
}

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const sizeMap = {
  small: 270,
  medium: 400,
  large: 600,
  full: '100%',
};

type PaperStyle = {
  width: number | string;
  boxShadow?: string;
};

let waitTimeout: NodeJS.Timeout;

const SearchBar: FC<SearchBarProps> = ({
  placeholder,
  size = 'medium',
  onSearch,
  applyShadow = true,
  results,
  onResultSelect,
  loading,
  searchOnKeyUp = false,
  onChange,
  searchDelay = 1000,
  ...inputProps
}) => {
  const MIN_LENGTH_TO_SEARCH = 3;
  const [value, setValue] = useState('');
  const [isResultsVisible, setResultsVisible] = useState(false);
  const [localResults, setLocalResults] = useState<SearchResultItem[]>(
    results || []
  );
  const searchBarRef = useRef<HTMLDivElement>(null);
  const paperStyle: PaperStyle = {width: sizeMap[size]};
  if (!applyShadow) {
    paperStyle['boxShadow'] = 'inherit';
  }
  const [isCloseVisible, setCloseVisible] = useState(false);

  // Memoize search delay to avoid recreating the function unnecessarily
  const search = React.useCallback(() => {
    clearTimeout(waitTimeout);
    waitTimeout = setTimeout(() => {
      onSearch && onSearch(value);
    }, searchDelay);
  }, [onSearch, value, searchDelay]);

  const handleResultSelect = (item: SearchResultItem) => {
    const updatedResults = localResults.map(result =>
      result.id === item.id ? {...result, selected: !result.selected} : result
    );
    setLocalResults(updatedResults);
    onResultSelect && onResultSelect({...item, selected: !item.selected});
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      searchBarRef.current &&
      !searchBarRef.current.contains(event.target as Node)
    ) {
      setResultsVisible(false);
    }
  };

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setValue(value);
    // Propagar el cambio si se proporciona onChange
    if (onChange) {
      onChange(event);
    }
  };

  const onClose = () => {
    setValue('');
    setResultsVisible(false);
  };

  const onKeyUp: React.KeyboardEventHandler<HTMLInputElement> = event => {
    if (
      (searchOnKeyUp && value.length >= MIN_LENGTH_TO_SEARCH) ||
      event.code === 'Enter'
    ) {
      search();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setLocalResults(results || []);
  }, [results]);

  useEffect(() => {
    const hasValue = value !== '';
    setCloseVisible(hasValue);

    if (hasValue) {
      search();
    }

    // Mostrar resultados solo si hay valor y hay resultados disponibles
    setResultsVisible(hasValue && results !== undefined && results.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, results]);

  return (
    <Box
      ref={searchBarRef}
      sx={{position: 'relative', width: paperStyle.width}}
      className="SearchBar"
    >
      <Paper
        sx={{
          p: '2px 4px',
          display: 'flex',
          alignItems: 'center',
          height: 56,
          border: '1px solid #ced4da',
          borderRadius: '12px',
          width: '100%',
          ...(paperStyle.boxShadow && {boxShadow: paperStyle.boxShadow}),
        }}
      >
        <IconButton sx={{p: '10px'}} aria-label="menu">
          <SearchIcon
            sx={loading ? {animation: `${spin} 1s linear infinite`} : undefined}
          />
        </IconButton>
        <InputBase
          value={value}
          onChange={handleOnChange}
          onKeyUp={onKeyUp}
          sx={{ml: 1, flex: 1}}
          placeholder={placeholder || ''}
          {...inputProps}
        />
        {isCloseVisible && (
          <IconButton
            data-testid="clearButton"
            sx={{p: '10px'}}
            aria-label="close"
            onClick={onClose}
          >
            <CloseIcon color="primary" />
          </IconButton>
        )}
      </Paper>
      {isResultsVisible && localResults && (
        <SearchBarResults
          items={localResults}
          onItemSelect={handleResultSelect}
        />
      )}
    </Box>
  );
};

export default SearchBar;
